import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { Heading } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import PlusIcon from '~/components/icons/PlusIcon';
import RefreshIcon from '~/components/icons/RefreshIcon';
import type {
  PolicyListResponse,
  PolicyListResponsePolicyListItemModel,
  PolicyRequest,
  PolicyResponse,
} from '~/server_shared/openapi/data-contracts';

import { useMockPolicyServer } from '../hooks/useMockPolicyServer';
import { usePolicyAPI } from '../hooks/usePolicyAPI';
import type { PolicyServerRecord } from '../types/internal';
import { getDisplayErrorMessage } from '../utils/errorHandling';

import PolicyDeleteDialog from './PolicyDeleteDialog';
import PolicyFormDialog from './PolicyFormDialog';
import PolicyList from './PolicyList';

interface PolicyManagementInterfaceProps {
  organizationId: number;
}

const PolicyManagementInterface: React.FC<PolicyManagementInterfaceProps> = ({ organizationId }) => {
  const [policies, setPolicies] = useState<PolicyListResponsePolicyListItemModel[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyServerRecord | undefined>(undefined);
  const [policyLoadingNumber, setPolicyLoadingNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { createPolicy, updatePolicy, deletePolicy, rebuildIndex, loading: apiLoading } = usePolicyAPI();
  const { loading: mockServerLoading } = useMockPolicyServer();

  const listPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get<PolicyListResponse>(
        `/sysconfig/api/v1/organizations/${organizationId}/mock_policy_server/policies`
      );
      const policiesData = response.data?.policies || [];
      setPolicies(policiesData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to fetch policies:', err);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    listPolicies();
  }, [listPolicies]);

  const handleAddPolicy = () => {
    setSelectedPolicy(undefined);
    setDialogMode('create');
    setError(null);
    setDialogOpen(true);
  };

  const handleViewPolicy = async (policy: PolicyListResponsePolicyListItemModel) => {
    try {
      setPolicyLoadingNumber(policy.policy_uuid);
      const response = await axios.get<PolicyResponse>(
        `/sysconfig/api/v1/organizations/${organizationId}/mock_policy_server/policies/${policy.policy_uuid}`
      );

      // Backend returns PolicyResponse with proper PolicyContainerModel structure
      const policyContainer = response.data.policy;

      // Create PolicyServerRecord combining list metadata with fetched policy container
      const policyServerRecord: PolicyServerRecord = {
        policyMetadata: policy,
        policyContainer,
      };

      setSelectedPolicy(policyServerRecord);
      setDialogMode('view');
      setError(null);
      setDialogOpen(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch policy details:', err);
    } finally {
      setPolicyLoadingNumber(null);
    }
  };

  const handleEditPolicy = async (policy: PolicyListResponsePolicyListItemModel) => {
    try {
      setPolicyLoadingNumber(policy.policy_uuid);
      const response = await axios.get<PolicyResponse>(
        `/sysconfig/api/v1/organizations/${organizationId}/mock_policy_server/policies/${policy.policy_uuid}`
      );

      // Backend returns PolicyResponse with proper PolicyContainerModel structure
      const policyContainer = response.data.policy;

      // Create PolicyServerRecord combining list metadata with fetched policy container
      const policyServerRecord: PolicyServerRecord = {
        policyMetadata: policy,
        policyContainer,
      };

      setSelectedPolicy(policyServerRecord);
      setDialogMode('edit');
      setError(null);
      setDialogOpen(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch policy details:', err);
    } finally {
      setPolicyLoadingNumber(null);
    }
  };

  const handleDeletePolicy = (policy: PolicyListResponsePolicyListItemModel) => {
    // Create PolicyServerRecord for consistency (no policyContainer needed for delete)
    const policyServerRecord: PolicyServerRecord = {
      policyMetadata: policy,
    };
    setSelectedPolicy(policyServerRecord);
    setDeleteDialogOpen(true);
  };

  const handleSavePolicy = async (policyRequest: PolicyRequest) => {
    try {
      if (dialogMode === 'create') {
        await createPolicy(policyRequest);
      } else if (dialogMode === 'edit') {
        await updatePolicy(selectedPolicy!.policyMetadata.policy_uuid, policyRequest);
      }
      setError(null);
      setDialogOpen(false);
      await listPolicies();
    } catch (err: unknown) {
      const errorMessage = getDisplayErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedPolicy) {
      try {
        await deletePolicy(selectedPolicy.policyMetadata.policy_uuid);
        setDeleteDialogOpen(false);
        setSelectedPolicy(undefined);
        await listPolicies();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete policy:', err);
      }
    }
  };

  const handleCloseDialog = () => {
    setSelectedPolicy(undefined);
    setDialogOpen(false);
    setError(null);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedPolicy(undefined);
    setDeleteDialogOpen(false);
  };

  const handleRebuildIndex = async () => {
    try {
      await rebuildIndex();
      // Reload policies to see updated data
      await listPolicies();
      // Optional: Show success message to user
      setError(null);
    } catch (err) {
      const errorMessage = getDisplayErrorMessage(err);
      setError(`Failed to rebuild index: ${errorMessage}`);
    }
  };

  const combinedLoading = apiLoading || mockServerLoading || loading;

  return (
    <CardDialog noCardTitle>
      <Grid container className="p-20">
        <Grid item xs={12} className="pb-12">
          <Heading variant={Heading.TYPES.H3}>Mock Policy Management</Heading>
        </Grid>
        <Grid item xs={12} className="inline-flex justify-end">
          <div className="flex gap-2">
            <Button
              color="secondary"
              onClick={handleRebuildIndex}
              disabled={combinedLoading}
              startIcon={<RefreshIcon className="text-gray-600" size={14} iconColor="currentColor" />}
            >
              Rebuild Index
            </Button>
            <Button
              color="primary"
              onClick={handleAddPolicy}
              disabled={combinedLoading}
              startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
            >
              Add New Policy
            </Button>
          </div>
        </Grid>
        <Grid item xs={12}>
          <PolicyList
            policies={policies}
            onView={handleViewPolicy}
            onEdit={handleEditPolicy}
            onDelete={handleDeletePolicy}
            policyLoadingNumber={policyLoadingNumber}
            loading={loading}
          />
        </Grid>
      </Grid>

      <PolicyFormDialog
        open={dialogOpen}
        mode={dialogMode}
        policy={selectedPolicy}
        onClose={handleCloseDialog}
        onSave={handleSavePolicy}
        loading={combinedLoading}
        error={error}
      />

      <PolicyDeleteDialog
        open={deleteDialogOpen}
        policy={selectedPolicy?.policyMetadata}
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        loading={apiLoading}
      />
    </CardDialog>
  );
};

export default PolicyManagementInterface;
